// R58-L-04 — per-level "bypass" hint. Renders a low-contrast tonal
// caption + an info-circle icon beneath an empty filter-level toggle
// group, so the analyst sees the structure of what they're filtering
// even when a level has no selection.
//
// `.vibe/08_STORY_BOOK_5.md §10 Q4/Q5` — locked decisions. The hint
// reads "All images" / "All kinds" / "All sides" / "All grades" per
// level.
//
// The component is presentational only — the parent decides when to
// render it (selection array is empty).
import { Box, Typography } from "@mui/material";
import { statusChipStyle } from "@aeriosiq/ui";
import { InfoIcon } from "@aeriosiq/ui/icons";
interface BypassHintProps {
  /** "All images" / "All kinds" / "All sides" / "All grades" */
  label: string;
  /**
   * Used to wire the hint to its parent toggle group for assistive
   * technology — caller passes the same id it set on the group's
   * `aria-describedby`. Optional; defaults to a slug derived from
   * `label` if not provided.
   */
  id?: string;
}

// SB6-ANA-FILT-06 (D14) — the reserved height (px) of the bypass-hint row.
// Each level renders a fixed-height slot for the hint (showing it only when
// the level is bypassed) so a level's overall height is constant whether or
// not its hint is visible — this keeps the filter strip dimensionally
// stable when options are toggled (no rebuild / no height jump).
export const BYPASS_HINT_SLOT_HEIGHT = 26;

/**
 * Fixed-height wrapper for the bypass hint. Always occupies
 * `BYPASS_HINT_SLOT_HEIGHT`px; renders the hint only when `show` is true.
 */
export function BypassHintSlot({
  show,
  label,
  id,
}: BypassHintProps & { show: boolean }): React.ReactElement {
  return (
    <Box
      sx={{
        minHeight: BYPASS_HINT_SLOT_HEIGHT,
        display: "flex",
        alignItems: "flex-start",
      }}
    >
      {show ? <BypassHint label={label} {...(id ? { id } : {})} /> : null}
    </Box>
  );
}

export function BypassHint({ label, id }: BypassHintProps): React.ReactElement {
  const hintId =
    id ?? `bypass-hint-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <Box
      id={hintId}
      role="note"
      aria-label={`Level is bypassed: ${label}`}
      data-testid={`bypass-hint-${label}`}
      sx={(theme) => {
        // SB6-ANA-FILT-01 / SB6-ANA-FILT-05 — this is a tonal chip, so it takes
        // D81's chip recipe wholesale via `statusChipStyle` instead of hand-
        // mixing a tint and then hand-darkening a foreground until it passed.
        //
        // The slot is `info`: the hint states that the level is at its default
        // ("all images"), which is informational state, not interaction — and
        // P3 reserves the accent for interaction, so the old brand-blue tint
        // cannot simply become aqua. Christian's ask [01:03:18] was that the
        // box read as "this IS the setting you have"; the recipe still carries
        // that with a tinted fill, and adds a hue keyline as a second,
        // non-colour signal (D26).
        //
        // The label and the icon are the NEUTRAL chip label, never the status
        // hue. That is the whole of D81: a tonal fill lifts the ground toward
        // the colour sitting on it, so a hue that carries the meaning cannot
        // also carry the contrast. The previous `#0058B8` / `#2997FF` pair was
        // exactly the darkened-hue workaround the system measured and rejected.
        const chip = statusChipStyle(theme, "info");
        return {
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          mt: 0.5,
          px: 0.75,
          py: 0.25,
          borderRadius: 0.75,
          backgroundColor: chip.backgroundColor,
          color: chip.color,
        };
      }}
    >
      <InfoIcon
        sx={(theme) => ({
          fontSize: 14,
          color: statusChipStyle(theme, "info").color,
        })}
        aria-hidden
      />
      <Typography
        component="span"
        sx={(theme) => ({
          fontSize: 11,
          lineHeight: 1.4,
          color: statusChipStyle(theme, "info").color,
          fontWeight: 600,
        })}
      >
        {label}
      </Typography>
    </Box>
  );
}
